/*
 *  ************************************************************************
 *  *  © [2015 - 2024] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useState } from 'react';
import { SvgHandler } from '../../../../svg-handler/svg-handler';
import downChevron from '../../../../../../assets/icons/down-arrow.svg';
import { getDateObj, textWithFallback } from '../../helpers';
import './styles.m.css';
import { getPath } from '../../../../../utils';
import { Invoices } from '../invoices';

const FALLBACK_DURATION = '<p>DURATION</p>';
const FALLBACK_AUTO_PAYMENTS = '<p>AUTO PAYMENTS</p>';
const FALLBACK_CHANGE_PLAN = '<p>Change Plan</p>';
const FALLBACK_AUTOPAY_OFF = '<p>OFF</p>';
const FALLBACK_AUTOPAY_ON = '<p>ON</p>';

const getAvailablePlansLink = ({ redirectUrl, target, subscriptionId, planId, sketchesHost, whiteListedUrlParams }) => {
  if (target !== 'web') return false;
    return getPath({
      sketchesHost,
      relativePath: '/available-plans',
      defaultParams: redirectUrl ? `subscriptionId=${subscriptionId}&planId=${planId}&redirectUrl=${redirectUrl}` : `subscriptionId=${subscriptionId}&planId=${planId}`,
      injectCustomParams: true,
      whiteListedUrlParams,
      swapDefaultCustomParams : true
    });
};

const ActivePlanDisplayCard = ({
  mode,
  isExpanded,
  canChangePlan,
  localizedMonths,
  isRecurring,
  buttonInlineStyles,
  manageSubscriptionsConfig,
  subscription,
  redirectUrl,
  index,
  target,
  sketchesHost,
  whiteListedUrlParams
}) => {
  const [showDetails, setShowDetails] = useState(isExpanded);
  const onClickHandler = () => {
    setShowDetails(!showDetails);
  };

  const {
    activeSubscription_duration,
    activeSubscription_autoPayments,
    activeSubscription_changePlanBtn,
    autoPayOffTxt,
    autoPayOnTxt,
    dl_invoice_enabled: invoicesEnabled
  } = manageSubscriptionsConfig;

  const {
    plan_name: planName,
    id: subscriptionId,
    subscription_plan_id: planId,
    duration_length: durationLength,
    duration_unit: durationUnit,
    plan_amount_currency: currency,
    plan_amount_cents: planAmountCents,
    end_timestamp: endTimestamp,
    start_timestamp: startTimestamp,
    invoices
  } = subscription;

  const { dd: startDD, mm: startMM, yy: startYY } = getDateObj(startTimestamp, localizedMonths);
  const { dd: endDD, mm: endMM, yy: endYY } = getDateObj(endTimestamp, localizedMonths);
  const autoPayText = isRecurring
    ? textWithFallback(autoPayOnTxt, FALLBACK_AUTOPAY_ON)
    : textWithFallback(autoPayOffTxt, FALLBACK_AUTOPAY_OFF);

  return (
    <div styleName={`active-plan-display-card ${mode}`} data-test-id={`manage-sub-active-plan-${index}`}>
      <div styleName='spaced-out' onClick={onClickHandler}>
        <div data-test-id={`manage-sub-active-plan-name-${index}`} dangerouslySetInnerHTML={{ __html: planName }} />
        <div styleName='active-plan-chevron' style={{ ...(showDetails ? { transform: 'rotate(180deg)' } : {}) }}>
          <SvgHandler xlinkHref={downChevron} width='24' height='24' />
        </div>
      </div>
      {showDetails ? (
        <>
          <div styleName={`spaced-out active-plan-duration-and-currency ${mode}`}>
            <div
              data-test-id={`manage-sub-active-plan-duration-${index}`}
              styleName='active-plan-duration'
            >{`${durationLength} ${durationUnit}`}</div>
            <div data-test-id={`manage-sub-active-plan-value-${index}`}>{`${currency} ${planAmountCents / 100}`}</div>
          </div>
          <div
            data-test-id={`manage-sub-active-plan-payment-labels-${index}`}
            styleName='spaced-out active-plan-duration-payment-labels'
          >
            <div
              styleName='active-plan-duration-label'
              dangerouslySetInnerHTML={{ __html: textWithFallback(activeSubscription_duration, FALLBACK_DURATION) }}
            />
            <div
              styleName='active-plan-payment-label'
              dangerouslySetInnerHTML={{
                __html: textWithFallback(activeSubscription_autoPayments, FALLBACK_AUTO_PAYMENTS)
              }}
            />
          </div>
          <div styleName='spaced-out'>
            <div data-test-id={`manage-sub-active-plan-start-end-dates-${index}`}>
              {`${startDD} ${startMM} ${startYY} - ${endDD} ${endMM} ${endYY}`}
            </div>
            <div styleName='active-plan-autopay-text' dangerouslySetInnerHTML={{ __html: autoPayText }} />
          </div>
          {canChangePlan && (
            <a
              style={buttonInlineStyles}
              styleName={`active-plan-change-plan-btn ${mode}`}
              dangerouslySetInnerHTML={{
                __html: textWithFallback(activeSubscription_changePlanBtn, FALLBACK_CHANGE_PLAN)
              }}
              href={getAvailablePlansLink({ redirectUrl, target, subscriptionId, planId, sketchesHost, whiteListedUrlParams })}
              data-test-id={`manage-sub-active-plan-change-btn-${index}`}
            />
          )}
          {invoicesEnabled && (
            <div styleName={`active-plan-download-invoice ${mode}`}>
              <Invoices
                invoices={invoices}
                subscriptionId={subscriptionId}
                target={target}
                invoicesConfig={manageSubscriptionsConfig}
              />
            </div>
          )}
        </>
      ) : (
        <div styleName='spaced-out'>
          <div>{`${startDD} ${startMM} ${startYY} - ${endDD} ${endMM} ${endYY}`}</div>
        </div>
      )}
    </div>
  );
};

export const ActiveSubscriptions = ({
  localizationConfig,
  manageSubscriptionsConfig,
  mode,
  memberSubscriptions,
  buttonInlineStyles,
  redirectUrl,
  target,
  sketchesHost,
  whiteListedUrlParams
}) => {
  const { enableLocalization = false, localizedElements = {} } = localizationConfig;
  const { months = {} } = enableLocalization ? localizedElements : {};
  const activeSubscriptions = memberSubscriptions
    .filter(s => s.status === 'active' && s.subscription_type !== 'dynamic_asset')
    .sort((a, b) => new Date(a.end_timestamp) - new Date(b.end_timestamp));

  return (
    <div styleName='active-plan-display-card-wrapper' data-test-id='manage-sub-active-plans'>
      {activeSubscriptions.map((sub, idx) => {
        const canChangePlan = sub.is_upgradable || sub.is_downgradable || sub.is_crossgradable;
        const { id: subscriptionId, plan_occurrence: planOccurrence } = sub;

        return (
          <ActivePlanDisplayCard
            key={subscriptionId}
            mode={mode}
            index={idx}
            manageSubscriptionsConfig={manageSubscriptionsConfig}
            subscription={sub}
            isExpanded={idx === 0}
            canChangePlan={canChangePlan}
            redirectUrl={redirectUrl}
            localizedMonths={months}
            isRecurring={planOccurrence === 'Recurring'}
            buttonInlineStyles={buttonInlineStyles}
            target={target}
            sketchesHost={sketchesHost}
            whiteListedUrlParams={whiteListedUrlParams}
          />
        );
      })}
    </div>
  );
};
